import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import Draggable from 'react-draggable';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    IconButton,
    LinearProgress,
    Chip,
    Tooltip,
    Paper,
    type PaperProps,
    useMediaQuery,
    useTheme
} from '@mui/material';
import {
    RiCloseLine,
    RiDownloadLine,
    RiPlayLine,
    RiFileLine,
    RiVideoLine,
    RiFileTextLine,
    RiExternalLinkLine,
    RiAlertLine,
    RiTimeLine,
    RiFullscreenLine,
    RiFullscreenExitLine
} from '@remixicon/react';
import LogoPJ from '@/assets/img/Logo_Nuevo_PJ.png';
import { extensionesValidas, handleDownloadFile, obtenerMimeType, obtenerTipoUsuario } from '../../utils/helpers';
import customAlert from '../CustomAlert';
import { useObtenerUrlConSasToken, useActivityRegisterWhileDownloadFile } from '@/modules/Archivo/hooks/useArchivos';
import type { TipoConsultaUsuarioEnum } from '@/modules/escritorio/types/enums/TipoUsuario.enum';
import { useUserData } from '@/shared/hooks/useUserData.hook';
import type { PerfilEntidad } from '@/shared/types/interfaces/user.interface';
import { storage } from '@/shared/utils/Const.utils';
import { StorageKeys } from '@/shared/types/enums/Storage.enum';
export interface FileViewerProps {
    /** URL del archivo a reproducir/descargar */
    fileUrl: string;
    /** Nombre del archivo */
    fileName: string;
    /**@deprecated Token SAS para autenticación (opcional) */
    sasToken?: string;
    /**@deprecated URL base para el visor de audio/video */
    baseViewerUrl?: string;
    /** Callback cuando se cierra el modal */
    onClose?: () => void;
    /** Si debe abrir automáticamente al montar */
    autoOpen?: boolean;
    /** Clase CSS personalizada */
    className?: string;
}

/** Cuando NO registra actividad */
interface FileViewerWithoutActivity extends FileViewerProps {
  enableActivityRegister?: false;
  // never le indica que esta propiedad no puede existir en este escenario
  fileId?: never;
}

/** Cuando SI registra actividad */
interface FileViewerWithActivity extends FileViewerProps {
  enableActivityRegister: true;
  /** id del archivo */
  fileId: string;
}

/** Discriminated Union.
 * permite que las props cambien según enableActivityRegister.
 */
export type FileViewerUnionProps = FileViewerWithoutActivity | FileViewerWithActivity;

// Tipos que se consideran de descarga directa (mantener comportamiento existente para estos)
enum ValidFileTypes {
    PDF = 'pdf',
    AVI = 'avi',
    MP4 = 'mp4',
    MP3 = 'mp3',
    DOC = 'doc',
    DOCX = 'docx',
    JPG = 'jpg',
    JPEG = 'jpeg',
    PNG = 'png'
}
const listaTiposDescargaAutomatica = [ValidFileTypes.AVI, ValidFileTypes.DOC, ValidFileTypes.DOCX];

/* PaperComponent para react-draggable - Defino fuera para evitar re-renderizados */
function DraggablePaperComponent(props: PaperProps) {
    const { isMaximized, nodeRef, ...other } = props as any;

    /* 
     Usamos key para forzar reinicio de posición al cambiar estado maximizado
     Esto evita conflictos de transformaciones y resetea a posición central (0,0) al restaurar
    */
    return (
        <Draggable
            key={isMaximized ? 'maximized' : 'draggable'}
            handle="#draggable-dialog-title"
            cancel={'[class*="MuiDialogContent-root"], .no-drag'}
            disabled={isMaximized}
            nodeRef={nodeRef}
        >
            <Paper {...other} ref={nodeRef} />
        </Draggable>
    );
}

interface FileTypeConfig {
    title: string;
    reason: string;
    icon: React.ReactNode;
    gradient: string;
    buttonColor: string;
    warningMessage: string;
    requiresToken: boolean;
}

const FileViewer: React.FC<FileViewerUnionProps> = ({
    fileUrl,
    fileName,
    sasToken,
    baseViewerUrl,
    onClose,
    autoOpen = false,
    className,
    enableActivityRegister = false,
    fileId,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm')); //<-- Detectar versión móvil (menor a 600px aprox)
    
    const [isOpen, setIsOpen] = useState(false);  // Cambiado a false siempre
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [playbackOption, setPlaybackOption] = useState<'none' | 'direct' | 'view' | 'download' | 'newTab'>('none');
    const [loadingMessage, setLoadingMessage] = useState('');
    const [hasAttemptedPlayback, setHasAttemptedPlayback] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [hasEnded, setHasEnded] = useState(false);
    const [fullFileUrl, setFullFileUrl] = useState<string>(''); // URL procesada con SAS (cacheada)
    const fullFileUrlRef = useRef<string>(''); // Ref para romper ciclo de dependencia en buildFileUrl
    const [pdfBlobUrl, setPdfBlobUrl] = useState<string>(''); // Blob URL para PDFs
    const hasResolvedUrlRef = useRef(false); // Evita múltiples mutaciones para la misma URL
    const videoRef = useRef<HTMLVideoElement>(null);
    const isInitializedRef = useRef(false);
    const lastProcessedRef = useRef<string>(''); // Rastrea último archivo procesado
    const { userData } = useUserData();
    const idUsuario = userData?.id || 0;
    const perfil: PerfilEntidad = storage.get(StorageKeys.PERFIL);
    const { id: idRelUsuarioEntidad, tipoConsulta: perfilTipoConsulta } = obtenerTipoUsuario(perfil, idUsuario);
    const { registerAndDownloadFile } = useActivityRegisterWhileDownloadFile();
    
    // Sincronizar ref con estado
    useEffect(() => {
        fullFileUrlRef.current = fullFileUrl;
    }, [fullFileUrl]);

    // Estados para movimiento y maximización del modal
    const [isMaximized, setIsMaximized] = useState(false);
    const nodeRef = useRef<HTMLDivElement>(null);
    const dialogRef = useRef<HTMLDivElement>(null);

    // Mutación para obtener URL con SAS token
    const { mutateAsync: obtenerUrlProcesada, isPending: isObteniendoUrl } = useObtenerUrlConSasToken();

    /* Obtener extensión del archivo */
    const getFileExtension = useCallback((filename: string): string => {
        if (!filename || typeof filename !== 'string') return '';
        const parts = filename.split('.');
        return parts.length > 1 ? parts.pop()!.toLowerCase().split(/\#|\?/)[0] : '';
    }, []);

    let extensionFileName = getFileExtension(fileName);
    const extensionEsValida = extensionesValidas().includes(extensionFileName);
    
    if (!extensionEsValida) {
        extensionFileName = getFileExtension(fileUrl);
        fileName = `${fileName}.${extensionFileName}`;
    }


    // Obtener MIME type
    const getMimeType = useCallback((filename: string): string => {
        const extension = getFileExtension(filename);
        let extensionExtraida = extension;
        if (!extensionesValidas().includes(extension)) {
            extensionExtraida = getFileExtension(fileUrl);
        }
        return obtenerMimeType(extensionExtraida);

        // switch (extension) {
        //     case 'pdf': return 'application/pdf';
        //     case 'avi': return 'video/avi';
        //     case 'mp4': return 'video/mp4';
        //     case 'mov': return 'video/quicktime';
        //     case 'wmv': return 'video/x-ms-wmv';
        //     case 'doc': return 'application/msword';
        //     case 'docx': return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        //     case 'jpg':
        //     case 'jpeg': return 'image/jpeg';
        //     case 'png': return 'image/png';
        //     case 'gif': return 'image/gif';
        //     default: return 'application/octet-stream';
        // }
    }, [getFileExtension]);

    // Configuraciones por tipo de archivo (replicando configuracionesTipo)
    const getFileTypeConfig = useCallback((extension: string): FileTypeConfig => {
        const configs: Record<string, FileTypeConfig> = {
            'avi': {
                title: 'Archivo de Video AVI',
                reason: 'Los archivos AVI contienen codecs de video que requieren reproductores específicos. El navegador puede no ser compatible con todos los formatos de codec.',
                icon: <RiVideoLine size={48} style={{ color: '#ef4444' }} />,
                gradient: 'linear-gradient(135deg, #fef2f2 0%, #fed7d7 100%)',
                buttonColor: '#ef4444',
                warningMessage: 'Archivo no compatible con navegador',
                requiresToken: true
            },
            'doc': {
                title: 'Documento Microsoft Word',
                reason: 'Los archivos DOC contienen formato propietario de Microsoft Word. Se recomienda descargar y abrir con un procesador de texto compatible.',
                icon: <RiFileTextLine size={48} style={{ color: '#3b82f6' }} />,
                gradient: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                buttonColor: '#3b82f6',
                warningMessage: 'Formato de documento especial',
                requiresToken: false
            },
            'docx': {
                title: 'Documento Microsoft Word (DOCX)',
                reason: 'Los archivos DOCX contienen formato moderno de Microsoft Word. Se recomienda descargar y abrir con un procesador de texto compatible.',
                icon: <RiFileTextLine size={48} style={{ color: '#3b82f6' }} />,
                gradient: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                buttonColor: '#3b82f6',
                warningMessage: 'Formato de documento especial',
                requiresToken: false
            },
            'pdf': {
                title: 'Documento PDF',
                reason: 'Archivo PDF listo para visualización. Se puede abrir en el navegador o descargar para uso offline.',
                icon: <RiFileLine size={48} style={{ color: '#059669' }} />,
                gradient: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                buttonColor: '#059669',
                warningMessage: 'Archivo seguro',
                requiresToken: true
            }
        };

        return configs[extension] || {
            title: 'Archivo Multimedia',
            reason: 'Archivo para reproducción o descarga. El tipo específico será determinado automáticamente.',
            icon: <RiFileLine size={48} style={{ color: '#6b7280' }} />,
            gradient: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
            buttonColor: '#6b7280',
            warningMessage: 'Archivo no reconocido',
            requiresToken: true
        };
    }, []);

    // Tipos que descargan automáticamente (solo los definidos explícitamente)
    const isAutoDownloadType = useCallback((extension: string): boolean => {
        return listaTiposDescargaAutomatica.includes(extension.toLowerCase() as ValidFileTypes);
    }, []);

    /* Descargar PDF y crear blob URL para evitar descarga automática */
    const createPdfBlobUrl = useCallback(async (url: string, fileName: string): Promise<string> => {
        try {
            const response = await fetch(url, {
                method: 'GET',
                mode: 'cors'
            });

            if (!response.ok) {
                throw new Error(`Error HTTP: ${response.status}`);
            }

            const blob = await response.blob();

            /* Asugurar que el blob descargado contenga el tipo MIME correcto */
            const mimetype = (blob?.type && blob?.type !== 'application/octet-stream')
                ? blob?.type
                : getMimeType(!blob?.type ? fileName : url);

            const blobUpdatedMimeType = new Blob([blob], { type: mimetype });
            const blobUrl = window.URL.createObjectURL(blobUpdatedMimeType);

            return blobUrl;
            // return '';
        } catch (error) {
            throw error;
        }
    }, []);

    /* Obtener / cachear URL procesada con SAS (todas las extensiones ya vienen con SAS desde backend) */
    const buildFileUrl = useCallback(async (): Promise<{ fileUrl: string, continue: boolean }> => {
        if (fileUrl.startsWith('blob:')) {
            setFullFileUrl(fileUrl);
            return { fileUrl, continue: true }; // <- Blob local, sin mutación. Archivo local
        }
        
        // Usar Ref para verificar cache sin añadir fullFileUrl a dependencias
        if (hasResolvedUrlRef.current && fullFileUrlRef.current) {
            return { fileUrl: fullFileUrlRef.current, continue: true }; // Cache existente
        }

        try {
            const response = await obtenerUrlProcesada(fileUrl);
            if (response.ok && response.urlProcessed) {
                hasResolvedUrlRef.current = true;
                setFullFileUrl(response.urlProcessed);

                return { fileUrl: response.urlProcessed, continue: true };
            }

            hasResolvedUrlRef.current = true;
            setFullFileUrl(fileUrl);

            return { fileUrl, continue: true };
        } catch (err) {
            hasResolvedUrlRef.current = true;
            setFullFileUrl(fileUrl);

            return { fileUrl, continue: false };
        }
    }, [fileUrl, obtenerUrlProcesada]);

    /* Descarga directa */
    const handleDirectDownload = useCallback(async () => {
        setIsLoading(true);
        try {
            const extension = getFileExtension(fileName);
            let fileUrl = '';

            /* Si es PDF y tenemos blob URL, usarlo directamente */
            if (extension === 'pdf' && pdfBlobUrl) {
                fileUrl = pdfBlobUrl;
            } else {
                /* Para otros archivos, obtener URL completa y descargar */
                const result = await buildFileUrl();
                if (!result.continue) {
                    customAlert({
                        icon: 'error',
                        titulo: 'No fue posible recuperar el archivo',
                        // mensaje: '.',
                        toast: true,
                    });
                    setIsLoading(false);
                    return;
                }
                
                fileUrl = result.fileUrl;
            }

            /* si esta habilitado el registro de actividades llamar el orquestador de lo contrario la funcion directa*/
            if(enableActivityRegister){
                await registerAndDownloadFile({
                    urlDocumento: fileUrl,
                    sasToken: '',
                    nombreDocumento: fileName,
                    idUsuario: idUsuario!,
                    tipoConsultaUsuario: perfilTipoConsulta!,
                    idDocumento: fileId!,
                    idRelUsuarioEntidad
                });
            } else {
                await handleDownloadFile({
                    urlDocumento: fileUrl,
                    sasToken: '',
                    nombreDocumento: fileName
                });
            }
        } catch (error) {
            /* Método alternativo de descarga */
            // await handleAlternativeDownload();
        } finally {
            setIsLoading(false);
        }
    }, [fileName, buildFileUrl, pdfBlobUrl, getFileExtension]);

    // Método alternativo de descarga con fetch
    const handleAlternativeDownload = useCallback(async () => {
        try {
            const result = await buildFileUrl();
            if (result.continue) {
                return;
            }
            const extension = getFileExtension(fileName);
            const typesWithoutToken = ['doc', 'docx'];
            const isTypeWithoutToken = typesWithoutToken.includes(extension);

            const headers = isTypeWithoutToken ? {
                'Accept': 'application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,*/*'
            } : {
                'Accept': '*/*'
            };

            const response = await fetch(result.fileUrl, {
                method: 'GET',
                headers: headers,
                mode: 'cors'
            });

            if (!response.ok) {
                throw new Error(`Error HTTP: ${response.status}`);
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            window.URL.revokeObjectURL(url);

            customAlert({
                titulo: 'Descarga Completada',
                mensaje: '<div>El archivo se ha descargado correctamente</div>',
                icon: 'success',
                toast: true
            });

        } catch (error) {
            customAlert({
                titulo: 'Error en la Descarga',
                mensaje: `<div>No se pudo descargar el archivo: ${error instanceof Error ? error.message : 'Error desconocido'}</div>`,
                icon: 'error',
                toast: true
            });
        }
    }, [fileName, buildFileUrl, getFileExtension]);

    // Abrir en nueva pestaña (replicando fnAbrirEnNuevaPestana)
    const handleOpenInNewTab = useCallback(async () => {
        try {
            const extension = getFileExtension(fileName);

            // Si es PDF y tenemos blob URL, usarlo directamente
            if (extension === 'pdf' && pdfBlobUrl) {
                const newWindow = window.open(pdfBlobUrl, '_blank', 'noopener,noreferrer');
            } else {
                // Para otros archivos, obtener URL completa
                const result = await buildFileUrl();
                const newWindow = window.open(result.fileUrl, '_blank', 'noopener,noreferrer');

                if (!newWindow) {
                    // Swal.fire({
                    //     title: 'Bloqueador de Ventanas Emergentes',
                    //     text: 'Su navegador bloqueó la apertura de la nueva pestaña. Por favor, permita ventanas emergentes para este sitio e intente nuevamente.',
                    //     icon: 'warning',
                    //     confirmButtonText: 'Entendido'
                    // });
                }
            }

            customAlert({
                mensaje: 'El archivo se ha abierto en una nueva pestaña',
                icon: 'info',
                toast: true
            });
        } catch (error) {
            customAlert({
                mensaje: 'No se pudo abrir el archivo en nueva pestaña',
                icon: 'error',
                toast: true
            });
        }
    }, [buildFileUrl, fileName, pdfBlobUrl, getFileExtension]);

    // Reproducción directa simplificada
    const handleDirectPlayback = useCallback(async () => {
        if (!videoRef.current) return;

        const video = videoRef.current;
        setIsLoading(true);
        setLoadingMessage('Preparando reproducción...');
        setError(null);

        try {
            const result = await buildFileUrl();

            // Configurar video source directamente
            video.src = result.fileUrl;

            // Event handlers
            const onCanPlay = () => {
                setIsLoading(false);
                setLoadingMessage('');
                setError(null);
                video.play().catch(playError => {
                    console.warn('Autoplay bloqueado:', playError);
                });
            };

            const onError = () => {
                setIsLoading(false);
                setError('No se pudo reproducir el archivo multimedia directamente. Intente descargar el archivo o abrirlo en nueva pestaña.');
            };

            // Remover event listeners anteriores
            video.removeEventListener('canplay', onCanPlay);
            video.removeEventListener('error', onError);

            // Agregar nuevos event listeners
            video.addEventListener('canplay', onCanPlay, { once: true });
            video.addEventListener('error', onError, { once: true });

            // Timeout de protección (5 segundos)
            const timeout = setTimeout(() => {
                if (isLoading) {
                    onError();
                }
            }, 5000);

            video.addEventListener('canplay', () => clearTimeout(timeout), { once: true });
            video.addEventListener('error', () => clearTimeout(timeout), { once: true });

            // Intentar cargar el video
            video.load();

        } catch (err) {
            setIsLoading(false);
            setError(err instanceof Error ? err.message : 'Error al cargar el archivo');
        }
    }, [fileName, buildFileUrl]);

    // Verificar si es un archivo multimedia (videos y audio se reproducen en modal)
    const isMultimediaFile = useCallback((extension: string): boolean => {
        const multimediaExtensions = ['avi', 'mp4', 'mov', 'wmv', 'flv', 'mkv', 'webm', 'mp3', 'wav', 'ogg'];
        return multimediaExtensions.includes(extension.toLowerCase());
    }, []);

    // Verificar si es un archivo de audio específicamente
    const isAudioFile = useCallback((extension: string): boolean => {
        const audioExtensions = ['mp3', 'wav', 'ogg'];
        return audioExtensions.includes(extension.toLowerCase());
    }, []);

    // Verificar si es un archivo visualizable en modal (documentos e imágenes)
    const isViewableInModal = useCallback((extension: string): boolean => {
        const viewableExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'doc', 'docx'];
        return viewableExtensions.includes(extension.toLowerCase());
    }, []);

    const isModalFile = useCallback((extension: string): boolean => {
        return isMultimediaFile(extension) || isViewableInModal(extension);
    }, [isMultimediaFile, isViewableInModal]);

    const handleFileAction = useCallback(async () => {
        const extension = getFileExtension(fileName);
        // const mimeType = getMimeType(fileName); // No usado actualmente

        if (!fileUrl) {
            setError('URL de archivo no válida');
            return;
        }

        if (isAutoDownloadType(extension)) {
            setShowConfirmationModal(true); //<-- importante: solo muestra confirmación, no descarga automática
            return;
        }

        if (isModalFile(extension)) {

            setIsOpen(true);
            if (isMultimediaFile(extension)) {
                setPlaybackOption('direct'); // Reproducción automática para multimedia
            } else {
                setPlaybackOption('view'); // Solo visualización para documentos
            }
        } else {
            /* Archivos no reconocidos se descargan */
            setShowConfirmationModal(true);
        }
    }, [fileName, fileUrl, getFileExtension, isAutoDownloadType, isModalFile, isMultimediaFile, isViewableInModal]);

    const handleClose = useCallback(() => {
        if (videoRef.current) {
            videoRef.current.pause();
            videoRef.current.src = '';
            videoRef.current.load();
        }

        if (pdfBlobUrl) {
            window.URL.revokeObjectURL(pdfBlobUrl);
            setPdfBlobUrl('');
        }

        setIsOpen(false);
        setShowConfirmationModal(false);
        setError(null);
        setPlaybackOption('none');
        setLoadingMessage('');
        setIsLoading(false);
        setHasAttemptedPlayback(false);
        setFullFileUrl('');
        lastProcessedRef.current = '';        //<-- Permite reabrir el mismo archivo
        hasResolvedUrlRef.current = false;

        isInitializedRef.current = false;

        setIsMaximized(false);
        console.log('FileViewer: Modal cerrado', isInitializedRef.current, lastProcessedRef.current || '(no hay archivo abierto)', hasResolvedUrlRef.current);
        onClose?.();
    }, [onClose, pdfBlobUrl]);

    const handleToggleMaximize = useCallback(() => {
        setIsMaximized(!isMaximized);
    }, [isMaximized]);


    const handleConfirmDownload = useCallback(async () => {
        await handleDirectDownload();
        // setShowConfirmationModal(false);
        handleClose();
    }, [handleDirectDownload, setShowConfirmationModal]);

    /* Auto inicio de reproducción/visualización cuando se abre el modal */
    const initializeViewer = useCallback(async () => {

        if (!isOpen) {
            return;
        }

        if (isInitializedRef.current) {
            return;
        }

        /* Marcar como inicializado | importante para que se pueda reabrir el mismo archivo */
        isInitializedRef.current = true;

        const result = await buildFileUrl();

        const extension = getFileExtension(fileName);

        if (extension === 'pdf') {
            
            if (isMobile) {
                /* En móviles usamos Google Docs Viewer con la URL directa, no necesitamos descargar el blob */
                setIsLoading(false);
            } else {
                /* Para Desktop, descargar y crear blob URL para evitar descarga automática / usar visor nativo del navegador a través de iframe */
                try {
                    const blobUrl = await createPdfBlobUrl(result.fileUrl, fileName);
                    setPdfBlobUrl(blobUrl);
                    /* No quitar loading aquí - esperar a que el iframe se cargue */
                } catch (error) {
                    console.error('Error creando blob URL para PDF:', error);
                    setError('No se pudo cargar el PDF. Intente descargar el archivo.');
                    setIsLoading(false);
                }
            }
        } else if (playbackOption === 'direct' && !hasAttemptedPlayback) {
            setHasAttemptedPlayback(true);

            setTimeout(() => handleDirectPlayback(), 100);
        } else if (playbackOption === 'view' && !hasAttemptedPlayback) {
            const imageExtensions = ['jpg', 'jpeg', 'png', 'gif'];
            const isImage = imageExtensions.includes(extension);

            if (!isImage) {
                setIsLoading(true);
                setHasAttemptedPlayback(true);

                setTimeout(() => {
                    setIsLoading(false);
                }, 1000);
            } else {
                setHasAttemptedPlayback(true);
            }
        }
    }, [isOpen, playbackOption, hasAttemptedPlayback, handleDirectPlayback, buildFileUrl, getFileExtension, createPdfBlobUrl, fileName]);

    useEffect(() => {
        initializeViewer();
    }, [initializeViewer]);

    /* Manejar auto-apertura del modal - ejecutar cuando autoOpen es true */
    useEffect(() => {
        if (!autoOpen || !fileUrl) return;

        const fileId = `${fileUrl}_${autoOpen}`;

        if (lastProcessedRef.current === fileId) return;

        lastProcessedRef.current = fileId;

        if (pdfBlobUrl) {
            window.URL.revokeObjectURL(pdfBlobUrl);
            setPdfBlobUrl('');
        }
          
        setHasAttemptedPlayback(false);
        setIsLoading(false);
        setError(null);
        setFullFileUrl('');
        setHasEnded(false);
        setIsPlaying(false);
        isInitializedRef.current = false;
        hasResolvedUrlRef.current = false;

        if (videoRef.current) {
            videoRef.current.pause();
            videoRef.current.src = '';
            videoRef.current.load();
        }

        const extension = getFileExtension(fileName);

        /* Tipos de descarga automática */
        if (isAutoDownloadType(extension)) {
            setShowConfirmationModal(true);
            return;
        }

        if (isModalFile(extension)) {
            setIsOpen(true);

            if (isMultimediaFile(extension)) {
                setPlaybackOption('direct');
            } else {
                setPlaybackOption('view');
            }
        } else {
            setShowConfirmationModal(true);
        }
    }, [autoOpen, fileUrl, fileName]); 

    /* Renderizar el botón de acción principal (icono de ojo) */
    const renderActionButton = () => {
        const extension = getFileExtension(fileName);
        const isMultimedia = isMultimediaFile(extension);
        const isViewable = isViewableInModal(extension);

        /* Determinar el texto del botón según el tipo */
        const getButtonText = () => {
            if (isLoading) return 'Cargando...';
            if (isMultimedia) return 'Reproducir';
            if (isViewable) return 'Visualizar';
            return 'Descargar';
        };

        return (
            <IconButton
                onClick={handleFileAction}
                disabled={isLoading}
                className={className}
                size="large"
                title={getButtonText()}
                sx={{
                    color: '#1976d2',
                    '&:hover': {
                        backgroundColor: 'rgba(25,118,210,.04)',
                        color: '#1565c0'
                    },
                }}
            >
                {/* <RiEyeLine size={16} /> */}
            </IconButton>
        );
    };

    /* Modal de confirmación de descarga */
    const renderDownloadConfirmationModal = () => {
        const extension = getFileExtension(fileName);
        const config = getFileTypeConfig(extension);

        return (
            <Dialog
                open={showConfirmationModal}
                onClose={(_, reason) => {
                    console.log('reason =>>', reason);
                    // if (reason === 'escapeKeyDown') {
                    setShowConfirmationModal(false);
                    // }
                }/*isLoading ? () => { } : handleClose*/}
                maxWidth="sm"
                fullWidth
                disableEscapeKeyDown={isLoading}
                PaperProps={{
                    sx: {
                        position: 'relative',
                        overflow: 'hidden'
                    }
                }}
            >
                {isLoading && (
                    <LinearProgress
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            zIndex: 1400
                        }}
                    />
                )}
                <DialogTitle sx={{
                    textAlign: 'center',
                    background: config.gradient,
                    color: '#374151',
                    pt: isLoading ? 3 : 2
                }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        {config.icon}
                        <Typography variant="h6" component="span">
                            {config.title}
                        </Typography>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ pt: 2 }}>
                    <Typography sx={{
                        textAlign: 'center',
                        color: '#374151',
                        fontSize: '14px',
                        lineHeight: 1.6,
                        mb: 2
                    }}>
                        {config.reason}
                    </Typography>

                    <Box sx={{
                        background: config.gradient,
                        p: 1.5,
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        mb: 2
                    }}>
                        <RiAlertLine size={16} style={{ color: config.buttonColor }} />
                        <Typography sx={{
                            color: '#374151',
                            fontWeight: 600,
                            fontSize: '12px'
                        }}>
                            {config.warningMessage}
                        </Typography>
                    </Box>

                    <Typography sx={{
                        color: '#6b7280',
                        fontSize: '12px',
                        textAlign: 'center'
                    }}>
                        <strong>Archivo:</strong> {fileName}
                    </Typography>
                </DialogContent>

                <DialogActions sx={{ justifyContent: 'center', gap: 1, pb: 2 }}>
                    <Button
                        onClick={handleClose}
                        variant="outlined"
                        disabled={isLoading}
                        sx={{ color: '#6b7280', borderColor: '#6b7280' }}
                    >
                        ✗ Cancelar
                    </Button>
                    <Button
                        onClick={handleConfirmDownload}
                        variant="contained"
                        disabled={isLoading}
                        sx={{
                            backgroundColor: config.buttonColor,
                            '&:hover': { opacity: 0.9 }
                        }}
                        startIcon={<RiDownloadLine />}
                    >
                        ✓ Sí, descargar
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    // Renderizar contenido del archivo según el tipo
    const renderFileContent = () => {
        const extension = getFileExtension(fileName);
        const isAudio = isAudioFile(extension);

        if (isMultimediaFile(extension)) {
            // Contenido multimedia (video/audio) - autoPlay solo para multimedia
            return (
                <video
                    ref={videoRef}
                    controls
                    autoPlay
                    preload="metadata"
                    controlsList="nodownload"
                    style={{
                        width: isAudio ? 'auto' : '100%',
                        maxWidth: isAudio ? '600px' : '100%',
                        maxHeight: isMaximized ? 'calc(100vh - 200px)' : '400px',
                        height: isAudio ? 'auto' : (isMaximized ? 'calc(100vh - 200px)' : 'auto'),
                        objectFit: 'contain',
                        display: isLoading ? 'none' : 'block',
                        backgroundColor: 'transparent',
                        opacity: hasEnded ? 0.3 : 1.0,
                        transition: 'opacity 0.5s ease-in-out',
                        margin: isAudio ? '0 auto' : '0'
                    }}
                    onClick={(e) => {
                        // Si el video ha terminado, prevenir cualquier acción al hacer clic
                        if (hasEnded) {
                            e.preventDefault();
                            e.stopPropagation();
                        }
                    }}
                    onError={() => setError('No se pudo cargar el archivo multimedia. Verifique el formato del archivo.')}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    onEnded={() => {
                        setIsPlaying(false);
                        setHasEnded(true);
                    }}
                    onLoadStart={() => setHasEnded(false)}
                >
                    Tu navegador no soporta la reproducción multimedia.
                </video>
            );
        } else if (extension === 'pdf') {
            const activeUrl = fullFileUrl || fileUrl;
            const isLocalBlob = activeUrl.startsWith('blob:');

            /* En móviles, mostrar botón para descargar SOLO si es blob local. Si es remoto, usar Google Docs */
            if (isMobile) {
                if (isLocalBlob) {
                    return (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            color: 'white',
                            p: 3,
                            textAlign: 'center'
                        }}>
                            <RiFileLine size={64} style={{ marginBottom: '16px', opacity: 0.8 }} />
                            <Typography variant="h6" sx={{ mb: 2 }}>
                                Vista previa no disponible
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 3, opacity: 0.8 }}>
                                La visualización de este archivo local no está disponible en la versión móvil.
                            </Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<RiDownloadLine />}
                                onClick={handleDirectDownload}
                                sx={{ mt: 1 }}
                            >
                                Descargar Documento
                            </Button>
                        </Box>
                    );
                }

                // Si es móvil y remoto, usar Google Docs Viewer
                return (
                    <iframe
                        src={`https://docs.google.com/viewer?url=${encodeURIComponent(activeUrl)}&embedded=true`}
                        style={{
                            width: '100%',
                            height: isMaximized ? 'calc(100vh - 200px)' : '600px',
                            border: 'none',
                            display: 'block',
                            backgroundColor: 'rgba(255,255,255,0.95)',
                            borderRadius: '4px'
                        }}
                        title={`PDF Viewer - ${fileName}`}
                        onLoad={() => setIsLoading(false)}
                        onError={() => setError('No se pudo cargar el PDF. Intente descargar el archivo.')}
                    />
                );
            }

            /* Visualizador PDF Desktop - usando iframe con blob URL para evitar descarga automática */
            // Solo renderizar cuando la blob URL esté disponible
            if (!pdfBlobUrl) {
                return (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '400px',
                        color: 'white'
                    }}>
                        <RiTimeLine size={48} className="animate-spin mb-4" />
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            Preparando PDF
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            Creando vista segura del documento...
                        </Typography>
                    </Box>
                );
            }

            return (
                <iframe
                    src={pdfBlobUrl}
                    style={{
                        width: '100%',
                        height: isMaximized ? 'calc(100vh - 200px)' : '600px',
                        border: 'none',
                        display: 'block',
                        backgroundColor: 'rgba(255,255,255,0.95)',
                        borderRadius: '4px'
                    }}
                    title={`PDF Viewer - ${fileName}`}
                    onLoad={() => setIsLoading(false)}
                    onError={() => setError('No se pudo cargar el PDF. Intente descargar el archivo.')}
                >
                    <p>No se pudo cargar el PDF. <a href={fullFileUrl || fileUrl} target="_blank">Abrir en nueva pestaña</a></p>
                </iframe>
            );
        } else if (extension === 'doc' || extension === 'docx') {
            /* Verificar si es un blob local (no accesible por Office Viewer externo) */
            const activeUrl = fullFileUrl || fileUrl;
            const isLocalBlob = activeUrl.startsWith('blob:');

            if (isLocalBlob) {
                return (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        color: 'white',
                        p: 3,
                        textAlign: 'center'
                    }}>
                        <RiFileTextLine size={64} style={{ marginBottom: '16px', opacity: 0.8, color: '#3b82f6' }} />
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            Vista previa no disponible
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 3, opacity: 0.8, maxWidth: '400px' }}>
                            Los documentos de Word locales o recién cargados no se pueden previsualizar aquí. 
                            Por favor, descargue el archivo para visualizarlo.
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<RiDownloadLine />}
                            onClick={handleDirectDownload}
                            sx={{ mt: 1 }}
                        >
                            Descargar Documento
                        </Button>
                    </Box>
                );
            }

            /* Visualizador para documentos Word usando Office Online */
            return (
                <iframe
                    src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(activeUrl)}`}
                    style={{
                        width: '100%',
                        height: isMaximized ? 'calc(100vh - 200px)' : '600px',
                        border: 'none',
                        display: isLoading ? 'none' : 'block',
                        backgroundColor: 'rgba(255,255,255,0.95)',
                        borderRadius: '4px'
                    }}
                    title={`Document Viewer - ${fileName}`}
                    onLoad={() => setIsLoading(false)}
                    onError={() => setError('No se pudo cargar el documento. Intente descargar el archivo.')}
                />
            );
        } else if (['jpg', 'jpeg', 'png', 'gif'].includes(extension)) {
            // Visualizador de imágenes - esperar a que la URL completa esté disponible
            if (!fullFileUrl) {
                return (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '400px',
                        color: 'white'
                    }}>
                        <RiTimeLine size={48} className="animate-spin mb-4" />
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            Preparando Imagen
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            Obteniendo URL segura de la imagen...
                        </Typography>
                    </Box>
                );
            }

            return (
                <img
                    src={fullFileUrl}
                    alt={fileName}
                    style={{
                        maxWidth: '100%',
                        maxHeight: isMaximized ? 'calc(100vh - 200px)' : '600px',
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain',
                        display: 'block',
                        borderRadius: '4px',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.3)'
                    }}
                    onLoad={() => setIsLoading(false)}
                    onError={() => setError('No se pudo cargar la imagen. Verifique el archivo.')}
                />
            );
        }

        return null;
    };

    /*PaperProps para evitar re-renderizados innecesarios del Dialog/Draggable */
    const paperProps = useMemo(() => ({
        isMaximized: isMaximized,
        nodeRef: nodeRef,
        sx: {
            backgroundColor: '#000',
            borderRadius: isMaximized ? 0 : 3,
            boxShadow: "0 8px 32px rgba(0, 80, 218, 0.15)",
            ...(isMaximized && {
                m: 0,
                maxHeight: '100vh',
                width: '100vw',
                height: '100vh'
            })
        }
    }), [isMaximized]);

    const renderViewerModal = () => {
        const extension = getFileExtension(fileName);
        const isMultimedia = isMultimediaFile(extension);

        return (
            <Dialog
                open={isOpen}
                onClose={(_, reason) => { if (!['backdropClick', 'escapeKeyDown'].includes(reason)) handleClose(); }}
                maxWidth={isMaximized ? false : "lg"}
                fullWidth={!isMaximized}
                fullScreen={isMaximized}
                PaperComponent={DraggablePaperComponent}
                PaperProps={paperProps as any}
                aria-labelledby="draggable-dialog-title"
            >
                <DialogTitle
                    id="draggable-dialog-title"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: '#1a1a1a',
                        color: 'white',
                        cursor: isMaximized ? 'default' : 'move',
                        userSelect: 'none'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden', mr: 1 }}>
                        {/* {isMultimedia ? (
                            <RiVideoLine size={24} color="#1976d2" />
                        ) : (
                            <RiFileLine size={24} color="#10b981" />
                        )} */}
                        {extension?.toLowerCase() === ValidFileTypes.AVI.toLowerCase() && (
                            <Chip
                                label={ValidFileTypes.AVI.toUpperCase()}
                                size="small"
                                sx={{
                                    backgroundColor: '#ef4444',
                                    color: 'white',
                                    fontSize: '10px'
                                }}
                            />
                        )}
                        {extension?.toLowerCase() === ValidFileTypes.PDF.toLowerCase() && (
                            <Chip
                                label={ValidFileTypes.PDF.toUpperCase()}
                                size="small"
                                sx={{
                                    backgroundColor: '#ef4444',
                                    color: 'white',
                                    fontSize: '10px'
                                }}
                            />
                        )}
                        {([ValidFileTypes.DOC.toLowerCase(), ValidFileTypes.DOCX.toLowerCase()].includes(extension?.toLowerCase())) && (
                            <Chip
                                label={extension?.toUpperCase()}
                                size="small"
                                sx={{
                                    backgroundColor: '#3b82f6',
                                    color: 'white',
                                    fontSize: '10px'
                                }}
                            />
                        )}
                        <Typography variant="h6" component="span" noWrap title={fileName}>
                            {fileName}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                        <Tooltip title={isMaximized ? "Restaurar" : "Maximizar"}>
                            <IconButton
                                className="no-drag"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleMaximize();
                                }}
                                size="small"
                                sx={{ color: 'white' }}
                            >
                                {isMaximized ? <RiFullscreenExitLine /> : <RiFullscreenLine />}
                            </IconButton>
                        </Tooltip>
                        <IconButton 
                            className="no-drag"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleClose();
                            }} 
                            size="small" 
                            sx={{ color: 'white' }}
                        >
                            <RiCloseLine />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{
                    backgroundColor: '#000',
                    p: 0,
                    position: 'relative',
                    height: isMaximized ? 'calc(100vh - 120px)' : 'calc(70vh - 120px)', // Ajustar por header y footer
                    overflow: 'hidden'
                }}>
                    {/* Logo de fondo */}
                    <Box
                        component="img"
                        src={LogoPJ}
                        alt="Logo Poder Judicial"
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '460px',
                            height: 'auto',
                            opacity: hasEnded ? 1.0 : (isPlaying ? 0.1 : 0.4),
                            zIndex: hasEnded ? 2 : 0,
                            filter: 'brightness(2) contrast(0.5)',
                            transition: 'opacity 0.5s ease-in-out',
                            pointerEvents: 'none'
                        }}
                    />
                    {/* Area principal de video */}
                    <Box sx={{
                        position: 'relative',
                        minHeight: isMaximized ? 'calc(100vh - 120px)' : '450px',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1
                    }}>
                        {isLoading && (
                            <Box sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(0,0,0,0.8)',
                                color: 'white',
                                zIndex: 10
                            }}>
                                <RiTimeLine size={48} className="animate-spin mb-4" />
                                <Typography variant="h6" sx={{ mb: 1 }}>
                                    {isMultimedia ? 'Preparando Reproducción' : 'Cargando Documento'}
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                    {loadingMessage || (isMultimedia ? 'Cargando archivo multimedia...' : 'Cargando documento...')}
                                </Typography>
                                <LinearProgress sx={{ width: '200px', mt: 2 }} />
                            </Box>
                        )}

                        {error ? (
                            <Box sx={{
                                textAlign: 'center',
                                color: 'white',
                                p: 4,
                                maxWidth: '400px'
                            }}>
                                <RiAlertLine size={48} style={{ color: '#fbbf24', marginBottom: '16px' }} />
                                <Typography variant="h6" sx={{ mb: 2, color: '#fbbf24' }}>
                                    No se pudo reproducir el archivo multimedia
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 3, opacity: 0.8 }}>
                                    {error}
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.6 }}>
                                    Intente descargar el archivo o abrirlo en nueva pestaña
                                </Typography>
                            </Box>
                        ) : (
                            renderFileContent()
                        )}
                    </Box>
                </DialogContent>

                <DialogActions sx={{ backgroundColor: '#1a1a1a', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                    {(() => {
                        const extension = getFileExtension(fileName);
                        const isMultimedia = isMultimediaFile(extension);
                        const isDocument = ['pdf', 'doc', 'docx'].includes(extension.toLowerCase());
                        
                        // Mostrar botón para multimedia o documentos
                        if (isMultimedia || isDocument) {
                            const buttonText = isMultimedia ? 'Reproducir' : 'Visualizar';
                            const tooltipText = isMultimedia ? 'Iniciar la reproducción de audio/video' : 'Iniciar la visualización del documento';
                            
                            return (
                                <Tooltip arrow defaultChecked placement='top' color='white' title={tooltipText}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<RiPlayLine />}
                                        onClick={() => {
                                            if (isMultimedia) {
                                                setPlaybackOption('direct');
                                                setHasAttemptedPlayback(false);
                                                setError(null);
                                                handleDirectPlayback();
                                            } else {
                                                setPlaybackOption('view');
                                                setHasAttemptedPlayback(false);
                                                setError(null);
                                                // Para documentos, reinicializar la visualización
                                                if (extension === 'pdf') {
                                                    // Para PDFs, recrear el blob URL si es necesario
                                                    if (!pdfBlobUrl && fullFileUrl) {
                                                        createPdfBlobUrl(fullFileUrl, fileName)
                                                            .then(blobUrl => setPdfBlobUrl(blobUrl))
                                                            .catch(error => setError('No se pudo cargar el PDF.'));
                                                    }
                                                }
                                                setIsLoading(false);
                                            }
                                        }}
                                        sx={{
                                            color: '#10b981',
                                            borderColor: '#10b981',
                                            '&:hover': { borderColor: '#059669', backgroundColor: 'rgba(16, 185, 129, 0.1)' }
                                        }}
                                    >
                                        {buttonText}
                                    </Button>
                                </Tooltip>
                            );
                        }
                        return null;
                    })()}
                    <Tooltip arrow placement='top' color='white' title="Descargar archivo">
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<RiDownloadLine />}
                            onClick={handleDirectDownload}
                            sx={{
                                color: '#3b82f6',
                                borderColor: '#3b82f6',
                                '&:hover': { borderColor: '#2563eb', backgroundColor: 'rgba(59, 130, 246, 0.1)' }
                            }}
                        >
                            Descargar
                        </Button>
                    </Tooltip>
                    <Tooltip arrow placement='top' color='white' title="Abrir en una nueva pestaña">
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<RiExternalLinkLine />}
                            onClick={handleOpenInNewTab}
                            sx={{
                                color: '#8b5cf6',
                                borderColor: '#8b5cf6',
                                '&:hover': { borderColor: '#7c3aed', backgroundColor: 'rgba(139, 92, 246, 0.1)' }
                            }}
                        >
                            Abrir en Nueva Pestaña
                        </Button>
                    </Tooltip>
                    <Tooltip arrow placement='top' color='white' title="Cerrar visor">
                        <Button
                            onClick={handleClose}
                            variant="outlined"
                            sx={{ color: 'white', borderColor: '#6b7280' }}
                        >
                            Cerrar
                        </Button>
                    </Tooltip>
                </DialogActions>
            </Dialog>
        );
    };

    return (
        <>
            {!autoOpen && renderActionButton()}
            {renderViewerModal()}
            {renderDownloadConfirmationModal()}
        </>
    );
};

export default FileViewer;